<script setup lang="ts">
import { type PollTailwindClasses } from '@@/bits/post_color'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import type { PollResult } from '@@/types'
import { computed, toRefs } from 'vue'

const props = withDefaults(
  defineProps<{
    result: PollResult
    index: number
    numResults: number
    darkMode?: boolean | 'auto'
    displayMode?: 'expanded' | 'default' | 'slideshow'
    isStream?: boolean
    pollColors: PollTailwindClasses
    decimalPercentage?: boolean
    pollScaleMultiplier?: number
  }>(),
  {
    darkMode: 'auto',
    displayMode: 'default',
    isStream: false,
    decimalPercentage: false,
    pollScaleMultiplier: 1,
  },
)

const {
  darkMode,
  displayMode,
  isStream,
  result,
  numResults,
  pollColors,
  decimalPercentage,
  index,
  pollScaleMultiplier,
} = toRefs(props)

const isDisplayModeExpanded = computed(() => displayMode.value === 'expanded')
const isDisplayModeSlideshow = computed(() => displayMode.value === 'slideshow')
const isDisplayModeExpandedOrSlideshow = computed(() => isDisplayModeExpanded.value || isDisplayModeSlideshow.value)
</script>
<template>
  <div :class="['flex flex-col mx-1']">
    <div
      :class="[
        'flex items-start justify-between flex-1 py-2 results-choice-slide',
        {
          'ms-10': isDisplayModeSlideshow && pollScaleMultiplier === 2.5,
          'ms-9': isDisplayModeSlideshow && pollScaleMultiplier === 2,
          'ms-4.5': isDisplayModeSlideshow && pollScaleMultiplier === 1,
          'ms-3': !isDisplayModeSlideshow && isStream,
          'ms-2': !isDisplayModeSlideshow && !isStream,
        },
      ]"
      :data-testid="`surfacePostPollChoiceResult${index + 1}`"
    >
      <p
        :class="[
          'me-1 break-word-anywhere',
          isDisplayModeExpandedOrSlideshow || isStream ? 'text-body' : 'text-body-small',
          {
            'text-dark-text-100': darkMode === false,
            'text-light-text-100': darkMode === true,
            'text-dark-text-100 dark:text-light-text-100': darkMode === 'auto',
          },
        ]"
      >
        {{ result.choice_text }}
      </p>
      <OzIcon
        v-if="result.user_voted"
        data-testid="surfacePostPollChoiceVotedIcon"
        :class="[
          'me-auto my-auto',
          {
            'text-dark-text-200': darkMode === false,
            'text-light-text-200': darkMode === true,
            'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
          },
        ]"
        :name="'checkmark_thicker'"
        :size="12"
      />
      <div :class="['ms-1 flex items-center my-auto', result.pollWinner ? 'poll-winner-slide' : 'poll-loser-slide']">
        <OzIcon
          v-if="result.pollWinner"
          :class="[
            'me-1',
            {
              [pollColors.pollWinnerIconText.light]: darkMode === false,
              [pollColors.pollWinnerIconText.dark]: darkMode === true,
              [pollColors.pollWinnerIconText.auto]: darkMode === 'auto',
            },
          ]"
          :name="'trophy'"
          :size="16"
        />
        <div
          :class="[
            {
              'me-10': isDisplayModeSlideshow && pollScaleMultiplier === 2.5,
              'me-9': isDisplayModeSlideshow && pollScaleMultiplier === 2,
              'me-4.5': isDisplayModeSlideshow && pollScaleMultiplier === 1,
              'me-2': !isDisplayModeSlideshow && isStream,
              'me-1': !isDisplayModeSlideshow && !isStream,
            },
            {
              'text-dark-text-200': darkMode === false,
              'text-light-text-200': darkMode === true,
              'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
            },
          ]"
          data-testid="surfacePostPollChoicePercentage"
        >
          {{ decimalPercentage ? result.percentage.toFixed(1) : result.percentage.toFixed(0) }}%
        </div>
      </div>
    </div>
    <div
      :class="[
        'relative',
        index !== numResults - 1 ? 'my-1' : 'mt-1',
        {
          'mx-7.5': isDisplayModeSlideshow && pollScaleMultiplier === 2.5,
          'mx-6': isDisplayModeSlideshow && pollScaleMultiplier === 2,
          'mx-3': isDisplayModeSlideshow && pollScaleMultiplier === 1,
          'mx-2': !isDisplayModeSlideshow && isStream,
          'mx-1': !isDisplayModeSlideshow && !isStream,
        },
      ]"
    >
      <div
        :class="[
          'h-1 absolute rounded-s-sm vote-bar-animation',
          index === numResults - 1 && isDisplayModeExpandedOrSlideshow ? '-top-2.5' : '-top-0.875',
          result.percentage === 100 ? 'rounded-e-sm' : 'rounded-e-md ',
          {
            [pollColors.closedPollChoicePercentageBackground.light]: darkMode === false,
            [pollColors.closedPollChoicePercentageBackground.dark]: darkMode === true,
            [pollColors.closedPollChoicePercentageBackground.auto]: darkMode === 'auto',
          },
        ]"
        :style="{
          width: result.percentage === 0 ? `4px` : `${result.percentage}%`,
        }"
      ></div>
      <OzDivider
        :class="[index === numResults - 1 && isDisplayModeExpandedOrSlideshow && '-translate-y-1.5']"
        :dark-mode="darkMode"
        :color-scheme="OzDividerColorScheme.Solid"
      />
    </div>
  </div>
</template>
<style lang="postcss" scoped>
.vote-bar-animation {
  animation: progressBar 0.7s cubic-bezier(0.5, 1.4, 0.4, 0.87);
  transform-origin: left;
  [dir='rtl'] & {
    transform-origin: right;
  }
}

@keyframes progressBar {
  0% {
    transform: scaleX(0);
  }
  100% {
    transform: scaleX(1);
  }
}

.poll-winner-slide {
  animation: pollWinnerSlide 0.3s ease-out;
  [dir='rtl'] & {
    animation: pollWinnerSlideRTL 0.3s ease-out;
  }
}

@keyframes pollWinnerSlide {
  0% {
    transform: translateX(25%);
  }
  100% {
    transform: translateX(0%);
  }
}

@keyframes pollWinnerSlideRTL {
  0% {
    transform: translateX(-25%);
  }
  100% {
    transform: translateX(0%);
  }
}

.poll-loser-slide {
  animation: pollLoserSlide 0.75s cubic-bezier(0.5, 1.55, 0.4, 0.87);
  [dir='rtl'] & {
    animation: pollLoserSlideRTL 0.75s cubic-bezier(0.5, 1.55, 0.4, 0.87);
  }
}

@keyframes pollLoserSlide {
  0% {
    transform: translateX(-500%);
    opacity: 0.1;
  }
  100% {
    transform: translateX(0%);
    opacity: 1;
  }
}

@keyframes pollLoserSlideRTL {
  0% {
    transform: translateX(500%);
    opacity: 0.1;
  }
  100% {
    transform: translateX(0%);
    opacity: 1;
  }
}

.results-choice-slide {
  animation: resultsChoiceSlide 0.2s ease-in-out;
  [dir='rtl'] & {
    animation: resultsChoiceSlideRTL 0.2s ease-in-out;
  }
}

@keyframes resultsChoiceSlide {
  0% {
    transform: translateX(28px);
  }
  100% {
    transform: translateX(0);
  }
}

@keyframes resultsChoiceSlideRTL {
  0% {
    transform: translateX(-28px);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
